import { create } from "zustand";

/**
 * Shopper cart and wishlist for published stores, kept per store (slug) in
 * the browser. Prices here are for display only — checkout reprices every
 * line on the server. Storage can be unavailable (private windows), so every
 * read and write is guarded and the store still works in memory.
 */

export interface CartLine {
  productId: string;
  slug: string;
  name: string;
  image?: string;
  priceMinor: number;
  options: Record<string, string>;
  quantity: number;
}

interface ShopState {
  carts: Record<string, CartLine[]>;
  wishlists: Record<string, string[]>;
  /** Whether the cart drawer is open. */
  drawerOpen: boolean;
  add: (store: string, line: CartLine) => void;
  setQuantity: (store: string, key: string, quantity: number) => void;
  remove: (store: string, key: string) => void;
  clear: (store: string) => void;
  toggleWishlist: (store: string, productId: string) => void;
  setDrawerOpen: (open: boolean) => void;
}

const STORAGE_KEY = "zirro-shop-v1";
const MAX_QUANTITY = 99;

/** Lines are the same item when product and chosen options match. */
export function lineKey(line: Pick<CartLine, "productId" | "options">): string {
  const opts = Object.keys(line.options)
    .sort()
    .map((k) => `${k}=${line.options[k]}`)
    .join("&");
  return `${line.productId}?${opts}`;
}

function load(): Pick<ShopState, "carts" | "wishlists"> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ShopState>;
      return { carts: parsed.carts ?? {}, wishlists: parsed.wishlists ?? {} };
    }
  } catch {
    // Storage blocked or corrupt — start empty.
  }
  return { carts: {}, wishlists: {} };
}

function save(state: Pick<ShopState, "carts" | "wishlists">) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ carts: state.carts, wishlists: state.wishlists }),
    );
  } catch {
    // Storage blocked — keep working in memory.
  }
}

export const useShop = create<ShopState>((set, get) => {
  const commit = (partial: Partial<Pick<ShopState, "carts" | "wishlists">>) => {
    set(partial);
    save({ ...get(), ...partial });
  };
  return {
    ...load(),
    drawerOpen: false,
    add: (store, line) => {
      const lines = [...(get().carts[store] ?? [])];
      const key = lineKey(line);
      const existing = lines.findIndex((l) => lineKey(l) === key);
      const current = lines[existing];
      if (current) {
        lines[existing] = {
          ...current,
          ...line,
          quantity: Math.min(MAX_QUANTITY, current.quantity + line.quantity),
        };
      } else {
        lines.push({ ...line, quantity: Math.min(MAX_QUANTITY, line.quantity) });
      }
      commit({ carts: { ...get().carts, [store]: lines } });
    },
    setQuantity: (store, key, quantity) => {
      const lines = (get().carts[store] ?? [])
        .map((l) => (lineKey(l) === key ? { ...l, quantity: Math.min(MAX_QUANTITY, quantity) } : l))
        .filter((l) => l.quantity > 0);
      commit({ carts: { ...get().carts, [store]: lines } });
    },
    remove: (store, key) => {
      const lines = (get().carts[store] ?? []).filter((l) => lineKey(l) !== key);
      commit({ carts: { ...get().carts, [store]: lines } });
    },
    clear: (store) => commit({ carts: { ...get().carts, [store]: [] } }),
    toggleWishlist: (store, productId) => {
      const list = get().wishlists[store] ?? [];
      const next = list.includes(productId) ? list.filter((id) => id !== productId) : [...list, productId];
      commit({ wishlists: { ...get().wishlists, [store]: next } });
    },
    setDrawerOpen: (open) => set({ drawerOpen: open }),
  };
});

const EMPTY_CART: CartLine[] = [];
const EMPTY_WISHLIST: string[] = [];

export function useCart(store: string): CartLine[] {
  return useShop((s) => s.carts[store] ?? EMPTY_CART);
}

export function useWishlist(store: string): string[] {
  return useShop((s) => s.wishlists[store] ?? EMPTY_WISHLIST);
}

export function cartCount(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity, 0);
}

export function cartSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.priceMinor * l.quantity, 0);
}
